import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  HomeChargingDriverToActivate,
  LinkyConsentApi,
} from "./linkyConsentsAPI";

export interface LinkyConsentsState {
  linkyConsentsStatus: QueryStatus;
  linkyConsents: LinkyConsentApi[];
  addHomeChargingDriversStatus: QueryStatus;
  sendDriverOnboardingLinkStatus: QueryStatus;
  onboardingLinkCooldownDriverUuids: string[];
}

const initialState: LinkyConsentsState = {
  linkyConsentsStatus: "idle",
  linkyConsents: [],
  addHomeChargingDriversStatus: "idle",
  sendDriverOnboardingLinkStatus: "idle",
  onboardingLinkCooldownDriverUuids: [],
};

export const getLinkyConsentsAsync = createAsyncThunk(
  "getLinkyConsentsAsync/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<LinkyConsentApi[]>(
      `electric_charges/linky_consents/company/${companyUuid}`,
    );
    return response.data;
  },
);

export const addHomeChargingDriversAsync = createAsyncThunk(
  "addHomeChargingDrivers/call",
  async (drivers: HomeChargingDriverToActivate[], thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) throw new Error("Company not selected");
    const axios = authAxios({ companyUuid: company.uuid });
    await axios.post(`electric_charges/drivers/company/${company.uuid}`, {
      drivers,
    });
  },
);

export const sendDriverOnboardingLinkAsync = createAsyncThunk(
  "sendDriverOnboardingLink/call",
  async (driverUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) throw new Error("Company not selected");
    const axios = authAxios({ companyUuid: company.uuid });
    await axios.post(`electric_charges/drivers/${driverUuid}/onboarding_link`);
  },
);

export const linkyConsentsSlice = createSlice({
  name: "linkyConsents",
  initialState,
  reducers: {
    resetLinkyConsentsStatus(state) {
      state.linkyConsentsStatus = "idle";
    },
    resetAddHomeChargingDriversStatus(state) {
      state.addHomeChargingDriversStatus = "idle";
    },
    resetSendDriverOnboardingLinkStatus(state) {
      state.sendDriverOnboardingLinkStatus = "idle";
    },
    endOnboardingLinkCooldown(state, action: PayloadAction<string>) {
      state.onboardingLinkCooldownDriverUuids =
        state.onboardingLinkCooldownDriverUuids.filter(
          (uuid) => uuid !== action.payload,
        );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLinkyConsentsAsync.pending, (state) => {
        state.linkyConsentsStatus = "processing";
      })
      .addCase(getLinkyConsentsAsync.fulfilled, (state, action) => {
        state.linkyConsentsStatus = "success";
        state.linkyConsents = action.payload;
      })
      .addCase(getLinkyConsentsAsync.rejected, (state) => {
        state.linkyConsentsStatus = "failed";
      })
      .addCase(addHomeChargingDriversAsync.pending, (state) => {
        state.addHomeChargingDriversStatus = "processing";
      })
      .addCase(addHomeChargingDriversAsync.fulfilled, (state) => {
        state.addHomeChargingDriversStatus = "success";
      })
      .addCase(addHomeChargingDriversAsync.rejected, (state) => {
        state.addHomeChargingDriversStatus = "failed";
      })
      .addCase(sendDriverOnboardingLinkAsync.pending, (state, action) => {
        state.sendDriverOnboardingLinkStatus = "processing";
        state.onboardingLinkCooldownDriverUuids.push(action.meta.arg);
      })
      .addCase(sendDriverOnboardingLinkAsync.fulfilled, (state) => {
        state.sendDriverOnboardingLinkStatus = "success";
      })
      .addCase(sendDriverOnboardingLinkAsync.rejected, (state) => {
        state.sendDriverOnboardingLinkStatus = "failed";
      });
  },
});

export const selectLinkyConsentsStatus = (state: RootState) =>
  state.linkyConsents.linkyConsentsStatus;

export const selectLinkyConsents = (state: RootState) =>
  state.linkyConsents.linkyConsents;

export const selectAddHomeChargingDriversStatus = (state: RootState) =>
  state.linkyConsents.addHomeChargingDriversStatus;

export const selectSendDriverOnboardingLinkStatus = (state: RootState) =>
  state.linkyConsents.sendDriverOnboardingLinkStatus;

export const selectOnboardingLinkCooldownDriverUuids = (state: RootState) =>
  state.linkyConsents.onboardingLinkCooldownDriverUuids;

export default linkyConsentsSlice.reducer;
